import { commandCenterPath } from '../../commandCenterRoutes'
// Olivium Governance Suite — card + module catalog (commit_1 foundation).
//
// The suite home surfaces ten cards. Three are already REAL, durable surfaces
// (Secure Access → sq_access_codes, Portal Factory / Project Portals →
// sq_project_portals) and keep their existing routes. Seven are the governance
// cards this lane deepens from Lane-1 stubs into working record workspaces.
//
// Honesty: each card declares its data reality so the home never implies a
// durable backend it does not have. No fabricated counts live here — metrics
// are derived from the governance model at render time (commit_2).

export const OLV_SUITE_ROOT = commandCenterPath('olivium')

/** The 7 governance record modules (mini-GitHub cards). */
export type GovModule =
  | 'audit'
  | 'integrity'
  | 'receipts'
  | 'risks'
  | 'access-logs'
  | 'secure-share'
  | 'permission-review'

export const GOV_MODULES: readonly GovModule[] = [
  'audit', 'integrity', 'receipts', 'risks', 'access-logs', 'secure-share', 'permission-review',
] as const

/** Where a card's data actually comes from — drives the honesty label. */
export type DataReality =
  | 'durable'          // persisted server-side today
  | 'durable_read'     // reads a real durable source; governed writes are local-session
  | 'local_session'    // interactive local-session ledger, labeled as such

export type GovvAccent =
  | 'identity' | 'structure' | 'verified' | 'attention' | 'denied' | 'secure'

export interface SuiteCard {
  id: string
  title: string
  blurb: string
  /** absolute route the card opens */
  to: string
  accent: GovvAccent
  reality: DataReality
  group: 'access' | 'governance'
  /** short honesty note shown under the card */
  honesty: string
}

export interface GovModuleDef {
  module: GovModule
  title: string
  /** route segment under the suite root */
  segment: string
  blurb: string
  accent: GovvAccent
  reality: DataReality
  /** singular record noun, e.g. "event", "block" */
  recordNoun: string
  honesty: string
}

/** The 7 governance cards — full definitions (routes, accents, honesty). */
export const GOV_MODULE_DEFS: Record<GovModule, GovModuleDef> = {
  audit: {
    module: 'audit', title: 'Audit Trail', segment: 'audit',
    blurb: 'Every governed action, hash-chained and correlated across modules.',
    accent: 'identity', reality: 'durable_read', recordNoun: 'event',
    honesty: 'Reads the real olv_audit_trail when reachable; local-session events are labeled.',
  },
  integrity: {
    module: 'integrity', title: 'Integrity Chain', segment: 'integrity',
    blurb: 'The tamper-evident block chain over the audit trail.',
    accent: 'structure', reality: 'durable_read', recordNoun: 'block',
    honesty: 'Verification calls the real olv_verify_event_chain RPC; no fake crypto.',
  },
  receipts: {
    module: 'receipts', title: 'Receipts', segment: 'receipts',
    blurb: 'Portable proof for governed events — bundle and share as evidence.',
    accent: 'verified', reality: 'durable_read', recordNoun: 'receipt',
    honesty: 'Reads the hash-chained receipt trail; bundles assembled locally are labeled.',
  },
  risks: {
    module: 'risks', title: 'Risk Flags', segment: 'risks',
    blurb: 'Detected governance risks — acknowledge, assign, mitigate, resolve.',
    accent: 'attention', reality: 'local_session', recordNoun: 'flag',
    honesty: 'Risk detection + mitigation run in a labeled local-session ledger.',
  },
  'access-logs': {
    module: 'access-logs', title: 'Access Logs', segment: 'access-logs',
    blurb: 'Access sessions and decisions — trust, challenge, suspend, terminate.',
    accent: 'structure', reality: 'durable_read', recordNoun: 'session',
    honesty: 'Backed by the real sq_access_codes spine; session controls are labeled.',
  },
  'secure-share': {
    module: 'secure-share', title: 'Secure Share', segment: 'secure-share',
    blurb: 'Controlled, constrained sharing of governed evidence and records.',
    accent: 'secure', reality: 'local_session', recordNoun: 'share',
    honesty: 'Share constraints modeled in a labeled local-session ledger; delivery is manual.',
  },
  'permission-review': {
    module: 'permission-review', title: 'Permission Review', segment: 'permission-review',
    blurb: 'Requested permission changes — approve, modify, deny, request changes.',
    accent: 'identity', reality: 'local_session', recordNoun: 'review',
    honesty: 'Review decisions recorded in a labeled local-session ledger.',
  },
}

export function govModulePath(module: GovModule): string {
  return `${OLV_SUITE_ROOT}/${GOV_MODULE_DEFS[module].segment}`
}

export function govRecordPath(module: GovModule, recordId: string): string {
  return `${govModulePath(module)}/${encodeURIComponent(recordId)}`
}

/** The three already-durable access surfaces shown alongside the 7 cards. */
export const ACCESS_CARDS: SuiteCard[] = [
  {
    id: 'secure-access', title: 'Secure Access',
    blurb: 'Issue controlled, project-bound access with a 9-gate preflight.',
    to: `${OLV_SUITE_ROOT}/secure-access`, accent: 'secure', reality: 'durable',
    group: 'access',
    honesty: 'Real, admin-gated sq_access_codes spine. Invitation delivery is manual (labeled).',
  },
  {
    id: 'portal-factory', title: 'Portal Factory',
    blurb: 'Register a project portal with readiness checks and modules.',
    to: `${OLV_SUITE_ROOT}/portal-factory`, accent: 'structure', reality: 'durable',
    group: 'access',
    honesty: 'Saved records in sq_project_portals. External client access is deferred (labeled).',
  },
  {
    id: 'project-portals', title: 'Project Portals',
    blurb: 'The registry of every registered portal and its readiness.',
    to: `${OLV_SUITE_ROOT}/portals`, accent: 'verified', reality: 'durable',
    group: 'access',
    honesty: 'Live registry over sq_project_portals.',
  },
]

/** The 7 governance cards as SuiteCards for the home grid. */
export const GOVERNANCE_CARDS: SuiteCard[] = GOV_MODULES.map((m) => {
  const d = GOV_MODULE_DEFS[m]
  return {
    id: d.module, title: d.title, blurb: d.blurb, to: govModulePath(m),
    accent: d.accent, reality: d.reality, group: 'governance' as const, honesty: d.honesty,
  }
})

/** All ten suite cards, access surfaces first. */
export const SUITE_CARDS: SuiteCard[] = [...ACCESS_CARDS, ...GOVERNANCE_CARDS]
